import React, { useEffect, useState } from "react";
import { api, fmtDate } from "../lib/api";
import { Plus, Trash2, Edit3, Tag, X } from "lucide-react";
import { toast } from "sonner";

const PLANS = ["pro_monthly", "pro_yearly", "founder_lifetime"];

export default function AdminPromos() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promos");
      setPromos(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load promos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const del = async (code) => {
    if (!window.confirm(`Delete promo ${code}? Existing checkouts already using this code remain valid.`)) return;
    try {
      await api.delete(`/admin/promos/${code}`);
      toast.success(`Deleted ${code}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/promos/${p.code}`, { active: !p.active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone bg-white px-8 py-6 flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold mb-1">// ADMIN · PROMOS</div>
          <h1 className="text-2xl font-bold text-graphite tracking-tight">Promo codes</h1>
        </div>
        <button
          data-testid="new-promo-btn"
          onClick={() => setDialog({ open: true, editing: null })}
          className="px-4 py-2.5 bg-paars text-graphite font-semibold text-xs tracking-wider uppercase hover:bg-paars-light flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> New code
        </button>
      </div>

      <div className="p-8">
        <div className="border border-stone bg-champagne overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone/50 border-b border-stone">
                {["Code", "Discount", "Plans", "Used", "Status", "Expires", "Actions"].map((h) => (
                  <th key={h} className={`px-3 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-graphite/55 ${["Discount", "Used"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-6 text-center text-graphite/45 font-mono text-xs">LOADING...</td></tr>
              )}
              {!loading && promos.length === 0 && (
                <tr><td colSpan={7} className="p-12 text-center text-graphite/55 text-sm">No promo codes yet. Create your first.</td></tr>
              )}
              {promos.map((p) => (
                <tr key={p.code} className="border-b border-stone/50 hover:bg-stone/30">
                  <td className="px-3 py-3 font-mono font-bold text-graphite tracking-wider">{p.code}</td>
                  <td className="px-3 py-3 text-right font-mono text-groen-dark">-${Number(p.discount_amount).toFixed(2)}</td>
                  <td className="px-3 py-3 font-mono text-xs text-graphite/65">{(p.applicable_plans || []).join(", ") || "all"}</td>
                  <td className="px-3 py-3 text-right font-mono">{p.uses_count}/{p.max_uses}</td>
                  <td className="px-3 py-3">
                    <button
                      data-testid={`toggle-${p.code}`}
                      onClick={() => toggleActive(p)}
                      className={`text-[10px] tracking-widest uppercase font-bold px-2 py-1 ${p.active ? "bg-groen text-graphite" : "bg-stone text-graphite/55"}`}
                    >
                      {p.active ? "Active" : "Paused"}
                    </button>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-graphite/55">{p.expires_at ? fmtDate(p.expires_at) : "Never"}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => setDialog({ open: true, editing: p })} className="text-graphite/55 hover:text-paars-dark" data-testid={`edit-${p.code}`}>
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button onClick={() => del(p.code)} className="text-graphite/55 hover:text-burgundy" data-testid={`del-${p.code}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {dialog.open && (
        <PromoDialog
          editing={dialog.editing}
          onClose={() => setDialog({ open: false, editing: null })}
          onSaved={() => { setDialog({ open: false, editing: null }); load(); }}
        />
      )}
    </div>
  );
}

function PromoDialog({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    code: editing?.code || "",
    discount_amount: editing?.discount_amount || 10,
    applicable_plans: editing?.applicable_plans || ["pro_monthly"],
    max_uses: editing?.max_uses || 100,
    active: editing?.active ?? true,
    expires_at: editing?.expires_at || "",
    notes: editing?.notes || "",
  }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const togglePlan = (pl) => {
    setForm((f) => ({
      ...f,
      applicable_plans: f.applicable_plans.includes(pl)
        ? f.applicable_plans.filter((x) => x !== pl)
        : [...f.applicable_plans, pl],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_amount: Number(form.discount_amount),
        max_uses: Number(form.max_uses),
        expires_at: form.expires_at || null,
      };
      if (editing) {
        const { code, ...patch } = payload;
        await api.patch(`/admin/promos/${editing.code}`, patch);
        toast.success(`Updated ${editing.code}`);
      } else {
        await api.post("/admin/promos", payload);
        toast.success(`Created ${payload.code}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-graphite/80 backdrop-blur-sm" onClick={onClose}>
      <form onSubmit={submit} className="bg-white border border-stone w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="promo-dialog">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-paars-dark" />
            <h2 className="text-base font-mono uppercase tracking-widest text-paars-dark">{editing ? "// EDIT PROMO" : "// NEW PROMO"}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-graphite/55 hover:text-graphite"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-graphite/55 font-bold block mb-1.5">Code *</label>
            <input
              data-testid="promo-code-field"
              required
              disabled={!!editing}
              value={form.code}
              onChange={(e) => set("code", e.target.value.toUpperCase().replace(/\s/g, ""))}
              className="w-full bg-champagne border border-stone px-3 py-2 text-sm font-mono uppercase tracking-widest disabled:opacity-60"
              placeholder="BLACKFRIDAY"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-graphite/55 font-bold block mb-1.5">Discount $</label>
              <input data-testid="promo-discount-field" required type="number" step="0.01" value={form.discount_amount} onChange={(e) => set("discount_amount", e.target.value)} className="w-full bg-champagne border border-stone px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-graphite/55 font-bold block mb-1.5">Max Uses</label>
              <input data-testid="promo-max-uses-field" required type="number" value={form.max_uses} onChange={(e) => set("max_uses", e.target.value)} className="w-full bg-champagne border border-stone px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-graphite/55 font-bold block mb-1.5">Applicable Plans</label>
            <div className="flex flex-wrap gap-2">
              {PLANS.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`plan-toggle-${p}`}
                  onClick={() => togglePlan(p)}
                  className={`px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold border ${form.applicable_plans.includes(p) ? "bg-paars text-graphite border-paars-dark" : "border-stone text-graphite/65 hover:bg-stone/40"}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-graphite/55 font-bold block mb-1.5">Expires At</label>
              <input type="date" value={form.expires_at ? form.expires_at.split("T")[0] : ""} onChange={(e) => set("expires_at", e.target.value || null)} className="w-full bg-champagne border border-stone px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-graphite/55 font-bold block mb-1.5">Active</label>
              <button type="button" onClick={() => set("active", !form.active)} className={`w-full py-2 text-xs font-semibold tracking-widest uppercase ${form.active ? "bg-groen text-graphite" : "bg-stone text-graphite/55"}`}>
                {form.active ? "ACTIVE" : "PAUSED"}
              </button>
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-graphite/55 font-bold block mb-1.5">Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full bg-champagne border border-stone px-3 py-2 text-sm" rows={2} placeholder="Internal notes (not shown to user)" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-stone">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-paars-dark/30 text-graphite/80 text-sm hover:bg-stone/50">Cancel</button>
          <button type="submit" data-testid="save-promo-btn" disabled={saving} className="px-5 py-2.5 bg-paars text-graphite font-semibold text-sm hover:bg-paars-light disabled:opacity-50">
            {saving ? "SAVING..." : editing ? "UPDATE" : "CREATE"}
          </button>
        </div>
      </form>
    </div>
  );
}
